import { isAwaitingCoachConfirmation } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-capabilities';
import type { CoachingPartnershipStatus } from '@efdigital/ll-addon-data';
import type { CoachingPlanner } from '@efdigital/ll-xceed-v2/utils/coaching/coachee/coaching-plan-manager';
import { array } from '@ember/helper';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import BsDropdown from 'ember-bootstrap/components/bs-dropdown';

interface CoachPartnershipActionsSignature {
  Args: {
    coachUserId: string;
    planner: CoachingPlanner;
    buttonClass: string;
    collapseMultipleActions?: boolean;
    /** Spoken on the Actions menu trigger so each card’s control is unique. */
    coachName?: string;
  };
}

const MENU_ITEM_CLASS = 'dropdown-item d-flex align-items-center gap-2 py-2';

/**
 * Every browse card renders an identical "Actions" button, so naming the coach
 * is what makes each one distinguishable in a screen reader's controls list.
 *
 * This goes on the trigger only. The menu itself renders as a plain container
 * with no ARIA role, and a label on a roleless element is ignored — so putting
 * one there would look thorough and do nothing.
 */
function actionsMenuLabel(coachName?: string): string {
  return coachName ? `Actions for ${coachName}` : 'Coach actions';
}

export class CoachPartnershipActions extends Component<CoachPartnershipActionsSignature> {
  @tracked loading = false;

  get isReassigned() {
    return this.args.planner.isCoachReassigned(this.args.coachUserId);
  }

  @cached
  get canTransitionToPending() {
    return (
      this.args.planner.canTransitionToStatus(this.args.coachUserId, 'pending')
        .outcome === 'allowed'
    );
  }

  @cached
  get canTransitionToChemistry() {
    return (
      this.args.planner.canTransitionToStatus(
        this.args.coachUserId,
        'chemistry'
      ).outcome === 'allowed'
    );
  }

  @cached
  get canTransitionToChosen() {
    return (
      this.args.planner.canTransitionToStatus(this.args.coachUserId, 'chosen')
        .outcome === 'allowed'
    );
  }

  @cached
  get canTransitionToDeclined() {
    return false;
    /*
    TODO: Implement declined status - this is supported but we don't want to make it too easy to decline a coach.
    Might need to do it through an admin request?
    return (
      this.args.planner.canTransitionToStatus(
        this.args.coachUserId,
        'declined'
      ).outcome === 'allowed'
    );
    */
  }

  @cached
  get canTransitionToAny() {
    return (
      this.canTransitionToPending ||
      this.canTransitionToChemistry ||
      this.canTransitionToChosen ||
      this.canTransitionToDeclined
    );
  }

  @cached
  get canBookChemistrySession() {
    return this.args.planner.canBookChemistrySession(this.args.coachUserId);
  }

  @cached
  get hasScheduledChemistrySession() {
    return (
      this.args.planner
        .getChemistrySessions(this.args.coachUserId)
        .filter(
          (session) =>
            session.status === 'scheduled' ||
            isAwaitingCoachConfirmation(session.status)
        ).length > 0
    );
  }

  get showBookChemistrySessionButton() {
    return this.canBookChemistrySession || this.hasScheduledChemistrySession;
  }

  setStatus = async (status: CoachingPartnershipStatus) => {
    this.loading = true;
    try {
      await this.args.planner.setStatusForCoach(this.args.coachUserId, status);
    } finally {
      this.loading = false;
    }
  };

  get hasMultipleActions() {
    let count = 0;
    if (this.showBookChemistrySessionButton) {
      count++;
    }
    if (this.canTransitionToPending) {
      count++;
    }
    if (this.canTransitionToChemistry) {
      count++;
    }
    if (this.canTransitionToChosen) {
      count++;
    }
    if (this.canTransitionToDeclined) {
      count++;
    }
    return count > 1;
  }

  get useDropdown() {
    return this.args.collapseMultipleActions && this.hasMultipleActions;
  }

  <template>
    {{#if this.isReassigned}}
      <p class="text-body-secondary small mb-0">
        This coach is no longer available to select.
      </p>
    {{else if this.useDropdown}}
      <BsDropdown as |ddm|>
        {{! `aria-haspopup="true"`, not `"menu"`: the items below are plain
            buttons and links, not `role="menuitem"`, so promising menu
            semantics would have screen readers offer menu navigation that
            does not work here. The generic value just says "opens something".
            `ember-bootstrap` handles `aria-expanded`. }}
        <ddm.button
          class="{{@buttonClass}}
            btn-primary d-inline-flex align-items-center justify-content-center gap-1"
          aria-label={{actionsMenuLabel @coachName}}
          aria-haspopup="true"
          aria-busy={{this.loading}}
        >
          Actions
          <iconify-icon icon="ph:caret-down" inline aria-hidden="true" />
        </ddm.button>

        <ddm.menu class="dropdown-menu-end shadow" as |menu|>
          {{#if this.showBookChemistrySessionButton}}
            <menu.item>
              <menu.linkTo
                @route="coaching.coachee.cohort.coaches.coach.book-chemistry"
                @models={{array @coachUserId}}
                @disabled={{this.loading}}
                class="{{MENU_ITEM_CLASS}}"
              >
                <iconify-icon
                  icon="ph:flask"
                  class="flex-shrink-0"
                  inline
                  aria-hidden="true"
                />
                {{#if this.hasScheduledChemistrySession}}
                  Chemistry session
                {{else}}
                  Book chemistry session
                {{/if}}
                <iconify-icon
                  icon="ph:arrow-right"
                  class="flex-shrink-0 ms-auto"
                  inline
                  aria-hidden="true"
                />
              </menu.linkTo>
            </menu.item>
          {{/if}}
          {{#if this.canTransitionToPending}}
            <menu.item>
              <button
                {{on "click" (fn this.setStatus "pending")}}
                type="button"
                class="{{MENU_ITEM_CLASS}} text-danger"
                disabled={{this.loading}}
                aria-busy={{this.loading}}
              >
                <iconify-icon
                  icon="ph:minus-circle"
                  class="flex-shrink-0"
                  inline
                  aria-hidden="true"
                />
                Remove from shortlist
              </button>
            </menu.item>
          {{/if}}
          {{#if this.canTransitionToChemistry}}
            <menu.item>
              <button
                {{on "click" (fn this.setStatus "chemistry")}}
                type="button"
                class={{MENU_ITEM_CLASS}}
                disabled={{this.loading}}
                aria-busy={{this.loading}}
              >
                <iconify-icon
                  icon="ph:plus-circle"
                  class="flex-shrink-0"
                  inline
                  aria-hidden="true"
                />
                Add to shortlist
              </button>
            </menu.item>
          {{/if}}
          {{#if this.canTransitionToChosen}}
            <menu.item>
              <button
                {{on "click" (fn this.setStatus "chosen")}}
                type="button"
                class={{MENU_ITEM_CLASS}}
                disabled={{this.loading}}
                aria-busy={{this.loading}}
              >
                <iconify-icon
                  icon="ph:check-circle"
                  class="flex-shrink-0"
                  inline
                  aria-hidden="true"
                />
                Choose coach
              </button>
            </menu.item>
          {{/if}}
          {{#if this.canTransitionToDeclined}}
            <menu.item>
              <button
                type="button"
                {{on "click" (fn this.setStatus "declined")}}
                class="{{MENU_ITEM_CLASS}} text-danger"
                disabled={{this.loading}}
                aria-busy={{this.loading}}
              >
                <iconify-icon
                  icon="ph:x-circle"
                  class="flex-shrink-0"
                  inline
                  aria-hidden="true"
                />
                Reject coach
              </button>
            </menu.item>
          {{/if}}
        </ddm.menu>
      </BsDropdown>
    {{else}}
      {{#if this.showBookChemistrySessionButton}}
        <LinkTo
          @route="coaching.coachee.cohort.coaches.coach.book-chemistry"
          @models={{array @coachUserId}}
          class="{{@buttonClass}}
            btn-primary d-inline-flex align-items-center gap-1
            {{if this.canTransitionToAny 'me-3'}}"
        >
          {{#if this.hasScheduledChemistrySession}}
            Chemistry session
          {{else}}
            Book chemistry session
          {{/if}}
          <iconify-icon icon="ph:arrow-right-bold" inline aria-hidden="true" />
        </LinkTo>
      {{/if}}
      {{#if this.canTransitionToPending}}
        <button
          class="{{@buttonClass}}
            btn-outline-danger d-inline-flex align-items-center gap-1"
          type="button"
          {{on "click" (fn this.setStatus "pending")}}
          disabled={{this.loading}}
          aria-busy={{this.loading}}
        >
          <iconify-icon icon="ph:minus-circle" inline aria-hidden="true" />
          Remove from shortlist
        </button>
      {{/if}}
      {{#if this.canTransitionToChemistry}}
        <button
          class="{{@buttonClass}}
            btn-primary d-inline-flex align-items-center gap-1"
          type="button"
          {{on "click" (fn this.setStatus "chemistry")}}
          disabled={{this.loading}}
          aria-busy={{this.loading}}
        >
          <iconify-icon icon="ph:plus-circle" inline aria-hidden="true" />
          Add to shortlist
        </button>
      {{/if}}
      {{#if this.canTransitionToChosen}}
        <button
          class="{{@buttonClass}}
            btn-primary d-inline-flex align-items-center gap-1"
          type="button"
          {{on "click" (fn this.setStatus "chosen")}}
          disabled={{this.loading}}
          aria-busy={{this.loading}}
        >
          <iconify-icon icon="ph:check-circle" inline aria-hidden="true" />
          Choose coach
        </button>
      {{/if}}
      {{#if this.canTransitionToDeclined}}
        <button
          class="{{@buttonClass}}
            btn-outline-danger d-inline-flex align-items-center gap-1"
          type="button"
          {{on "click" (fn this.setStatus "declined")}}
          disabled={{this.loading}}
          aria-busy={{this.loading}}
        >
          <iconify-icon icon="ph:x-circle" inline aria-hidden="true" />
          Reject coach
        </button>
      {{/if}}
    {{/if}}
  </template>
}
